import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { FullAccessRoleIcon, CanPayBillsRoleIcon, ViewerRoleIcon } from "../../../../../../assets/icons"
import { useLazyGetRolesQuery } from "../../../../../../service/role-service"
import { useNewTeamMemberInvitationMutation } from "../../../../../../service/team-management-service"
import { roleDescriptions, corporateRoleNames, corporateRoleDescriptions } from "../../team-management.constant"
import { CustomerType } from "../../../../../../model/customer/customer-type"
import { RootState } from "../../../../../../stores/store"
import styles from "./invite-member-modal.module.scss"

const ROLE_ICONS: Record<string, React.ReactElement> = {
	"Full Access": <FullAccessRoleIcon />,
	"Can Pay Bills": <CanPayBillsRoleIcon />,
	"Viewer": <ViewerRoleIcon />,
}

const getRoleIcon = (backendName: string) => ROLE_ICONS[backendName] ?? null

interface RoleOption {
	value: string
	label: string
	description: string
}

interface InviteMemberModalProps {
	onClose: (invited?: boolean) => void
}

interface FormValues {
	email: string
	firstName: string
	lastName: string
}

export const InviteMemberModal = ({ onClose }: InviteMemberModalProps) => {
	const { t } = useTranslation()
	const customerType = useSelector((state: RootState) => state.user.customerType)
	const isCorporate = customerType === CustomerType.Corporate

	const [getTeamMemberRoles] = useLazyGetRolesQuery()
	const [newTeamMemberInvitation, { isLoading: isSending }] = useNewTeamMemberInvitationMutation()

	const [roleOptions, setRoleOptions] = useState<RoleOption[]>([])
	const [selectedRole, setSelectedRole] = useState<RoleOption | null>(null)
	const [isRoleListOpen, setIsRoleListOpen] = useState(false)
	const [roleError, setRoleError] = useState<string | null>(null)

	const getRoleDisplayName = (backendName: string) =>
		isCorporate ? (corporateRoleNames[backendName] ?? backendName) : backendName

	const getRoleDisplayDescription = (backendName: string) =>
		isCorporate ? (corporateRoleDescriptions[backendName] ?? "") : (roleDescriptions[backendName] ?? "")

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<FormValues>({
		defaultValues: { email: "", firstName: "", lastName: "" }
	})

	useEffect(() => {
		const fetchRoles = async () => {
			try {
				const result = await getTeamMemberRoles({ Page: 1, PageSize: 100 }).unwrap()
				if (result) {
					setRoleOptions(result.items.map(x => ({
						value: x.id,
						label: x.name,
						description: roleDescriptions[x.name] || ""
					})))
				}
			} catch (error: any) {
				toast.error(error?.data?.title || t("account.web.failedToFetchRoles"))
			}
		}
		fetchRoles()
	}, [getTeamMemberRoles])

	const onSubmit = async (data: FormValues) => {
		if (!selectedRole) {
			setRoleError(t("account.web.pleaseSelectRoleForMember"))
			return
		}

		try {
			await newTeamMemberInvitation({
				email: data.email.trim(),
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				roleId: selectedRole.value
			}).unwrap()

			toast.success(t("account.web.invitationSentTo", { email: data.email.trim() }))
			onClose(true)
		} catch (error: any) {
			toast.error(error?.data?.title || t("account.web.unableToSendInvitation"))
		}
	}

	const selectRole = (role: RoleOption) => {
		setSelectedRole(role)
		setRoleError(null)
		setIsRoleListOpen(false)
	}

	return (
		<div className={styles.overlay} onClick={() => onClose()}>
			<div className={styles.sheet} onClick={e => e.stopPropagation()}>
				<div className={styles.toolbar}>
					<button type="button" className={styles.closeButton} onClick={() => onClose()} disabled={isSending} aria-label={t("account.web.close")}>
						×
					</button>
					<h2 className={styles.title}>{t("corporate.inviteMember")}</h2>
				</div>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className={styles.body}>
						<div className={styles.fieldsCard}>
							<div className={styles.inputField}>
								<label className={styles.inputLabel}>{t("common.labels.emailAddress")}</label>
								<input
									type="email"
									className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
									placeholder={t("account.web.emailAddressPlaceholder")}
									disabled={isSending}
									{...register("email", {
										required: t("account.web.enterMemberEmailAddress"),
										pattern: {
											value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
											message: t("corporate.enterValidEmail")
										}
									})}
								/>
								{errors.email && <p className={styles.errorText}>{errors.email.message}</p>}
							</div>

							<div className={styles.inputField}>
								<label className={styles.inputLabel}>{t("common.labels.firstName")}</label>
								<input
									type="text"
									className={`${styles.input} ${errors.firstName ? styles.inputError : ""}`}
									placeholder={t("corporate.firstNameExample")}
									disabled={isSending}
									{...register("firstName", { required: t("account.web.enterMemberName") })}
								/>
								{errors.firstName && <p className={styles.errorText}>{errors.firstName.message}</p>}
							</div>

							<div className={styles.inputField}>
								<label className={styles.inputLabel}>{t("common.labels.surname")}</label>
								<input
									type="text"
									className={`${styles.input} ${errors.lastName ? styles.inputError : ""}`}
									placeholder={t("corporate.lastNameExample")}
									disabled={isSending}
									{...register("lastName", { required: t("account.web.enterMemberName") })}
								/>
								{errors.lastName && <p className={styles.errorText}>{errors.lastName.message}</p>}
							</div>

							<hr className={styles.divider} />

							<div className={styles.inputField}>
								<label className={styles.inputLabel}>{t("corporate.assingRole")}</label>
								<div className={styles.roleDropdownWrapper}>
									{isRoleListOpen && (
										<div className={styles.roleList}>
											{roleOptions.map(role => (
												<button
													key={role.value}
													type="button"
													className={`${styles.roleListItem} ${selectedRole?.value === role.value ? styles.roleListItemSelected : ""}`}
													onClick={() => selectRole(role)}
												>
													<span className={styles.roleListItemIcon}>
														{getRoleIcon(role.label)}
													</span>
													<span className={styles.roleListItemContent}>
														<span className={styles.roleListItemTitle}>{getRoleDisplayName(role.label)}</span>
														<span className={styles.roleListItemDescription}>{getRoleDisplayDescription(role.label)}</span>
													</span>
												</button>
											))}
										</div>
									)}
									<button
										type="button"
										className={styles.roleTrigger}
										onClick={() => setIsRoleListOpen(open => !open)}
										disabled={isSending}
									>
										<span className={`${styles.roleIconCircle} ${selectedRole ? styles.roleIconCircleSelected : ""}`}>
											{selectedRole ? getRoleIcon(selectedRole.label) : "+"}
										</span>
										<span className={styles.roleTriggerText}>
											<span className={`${styles.roleTriggerTitle} ${!selectedRole ? styles.roleTriggerMuted : ""}`}>
												{selectedRole ? getRoleDisplayName(selectedRole.label) : t("account.web.selectARole")}
											</span>
											<span className={`${styles.roleTriggerDescription} ${!selectedRole ? styles.roleTriggerMuted : ""}`}>
												{selectedRole
													? getRoleDisplayDescription(selectedRole.label)
													: t("corporate.chooseWhatMemberIsAllowed")}
											</span>
										</span>
										<span className={`${styles.roleChevron} ${isRoleListOpen ? styles.roleChevronOpen : ""}`}>
											<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
												<path d="M7 9l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										</span>
									</button>
								</div>
								{roleError && <p className={styles.errorText}>{roleError}</p>}
							</div>
						</div>

						<button type="submit" className={styles.sendButton} disabled={isSending}>
							{isSending ? t("account.web.sending") : t("corporate.sendInvitation")}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
